<template>
  <DevTestOnly>
    <div v-if="error">
      <DevXDebug :error="error" />
      <pre>{{ error }}</pre>
    </div>
  </DevTestOnly>
  <SearchFilters :results="data?.data" :flex-search="props.flexSearch" :show-only-lastminute-objects="props.showOnlyLastminuteObjects" />
  <div id="search-map" class="mt-6 w-full mx-auto px-0">
    <div class="lg:flex lg:items-stretch w-full container lg:max-w-full mx-auto">
      <div id="container-search" class="lg:flex-none w-full lg:w-[750px] xl:w-[800px] 1xl:w-[1060px] 2xl:w-[1140px] px-8">
        <div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-2 1xl:grid-cols-3 2xl:grid-cols-3 gap-4">
          <SearchResults
            :data="data?.data"
            :pending="pending"
            :page-title="props.pagedata?.details?.header?.h1 ?? null"
            :highlighted-object-nr="highlightedObjectNr"
            :highlighted-object-icon-url="highlightedObjectIconUrl"
            :show-only-lastminute-objects="props.showOnlyLastminuteObjects"
          />
        </div>
        <div class="m-5 p-5 text-center">
          <SharedPagination
            :url-prefix="props.path"
            :current-page="paginationCurrentPage"
            :total-items="data?.data?.hits ?? 0"
            :items-per-page="searchParamsStore.rows"
            :disabled="pending"
          />
        </div>
        <div v-if="props.pagedata?.details?.header" class="m-5 p-5 text-left">
          <!-- eslint-disable vue/no-v-html -->
          <div
            v-if="props.pagedata?.details?.header?.text"
            class="search-page-content"
            v-html="props.pagedata.details.header.text"
          />
          <!-- eslint-enable vue/no-v-html -->
        </div>
      </div>
      <ClientOnly>
        <div v-if="mapStore.visible && !mapStore.mapModalOpen" id="map-search" class="lg:flex-1 lg:block" style="position: relative;">
          <div class="sticky top-0" data-behaviour="SearchResultMapSticky">
            <div class="relative w-full custom-map-height">
              <div class="absolute h-full w-full inset-0 custom-region-map-width custom-region-map bg-neutral-50">
                <SearchResultsMap
                  :data="data?.data"
                  :pending="pending"
                  @marker-mouse-over="mapMarkerMouseOver"
                  @marker-mouse-out="mapMarkerMouseOut"
                  @marker-click="mapMarkerClick"
                />
              </div>
            </div>
          </div>
        </div>
      </ClientOnly>
    </div>
  </div>
  <!-- Map Modal (Android + Desktop narrow viewport) -->
  <UiModal
    v-if="!mapStore.isAppleDevice"
    :modal-id="mapModalId"
    :open="mapStore.mapModalOpen"
    width="w-full"
    :fullscreen="true"
    variant="map"
    @hidden="handleModalClose"
  >
    <template #head>
      {{ t('map') }}
    </template>
    <div class="h-dvh relative">
      <SearchResultsMapModal
        v-if="mapStore.mapModalOpen"
        ref="modalMapRef"
        :data="data?.data"
        :pending="pending"
        @marker-mouse-over="mapMarkerMouseOver"
        @marker-mouse-out="mapMarkerMouseOut"
        @marker-click="mapMarkerClick"
      />
    </div>
  </UiModal>
</template>

<script setup lang="ts">
import type { Ref } from 'vue'
import type MapMarkerEventData from '~/types/search/MapMarkerEventData'
import { useSearchParamsStore } from '~~/stores/searchParams'
import { WebStorage } from '~/utils/WebStorage'
import UrlHashSearchParams from '~/utils/search/UrlHashSearchParams'
import SearchParamsHelper from '~/utils/search/SearchParamsHelper'
import { useSearchResultsMapStore } from '~~/stores/searchResultsMap'
import { SEARCH_DATA_REF_KEY } from '~/composables/search/useSearchResultsMapKeys'
import { BREAKPOINT_LG_MIN_QUERY, BREAKPOINT_LG_PX } from '~/utils/breakpoints'

// TODO: Use the pagedata prop for the predefined/'static' SERPs
const props = defineProps({
  path: {
    type: String,
    default: '/search/'
  },
  pagedata: {
    type: Object,
    default: null
  },
  paginationCurrentPage: {
    type: Number,
    default: 1
  },
  showOnlyLastminuteObjects: {
    type: Boolean,
    default: false
  },
  flexSearch: {
    type: Boolean,
    default: true
  }
})

const searchParamsStore = useSearchParamsStore()
const urlHashSearchParams = new UrlHashSearchParams()

searchParamsStore.page = props.paginationCurrentPage - 1
searchParamsStore.only_lastminute_objects = props.showOnlyLastminuteObjects
if (import.meta.server) {
  searchParamsStore.is_static_page = false
  if (props.pagedata?.details?.header?.params) {
    const mergedParams = {
      page: 0,
      is_static_page: true,
      ...props.pagedata.details.header.params
    }
    searchParamsStore.resetToDefaultState(false, props.showOnlyLastminuteObjects)
    searchParamsStore.$patch(mergedParams)
  }

  const webStorage = new WebStorage()
  const cookieParams = useCookie(
    webStorage.items.SearchParams.getUseCookieKey(),
    webStorage.items.SearchParams.getUseCookieOptions()
  )
  cookieParams.value = webStorage.items.SearchParams.getUseCookieValue(searchParamsStore.$state)
}

const { $backend } = useNuxtApp()
const { data, pending, error, refresh } = await $backend.search.search(searchParamsStore.$state)

// Provide raw useFetch data ref so map composable can watch it directly.
// This bypasses Vue prop-update batching that causes markers to go stale
// after the map modal is closed and reopened.
provide(SEARCH_DATA_REF_KEY, data)
const { links } = usePortalData()
const { translate: t } = useTranslations('SearchResults')
const mapModalId = useComponentId('modal-search-map')
const mapStore = useSearchResultsMapStore()
const modalMapRef = ref()

// Debounce timer for handleModalClose
let handleModalCloseTimer: ReturnType<typeof setTimeout> | null = null

// Limits value at the moment the modal was opened — used to detect in-modal map panning
let limitsBeforeModalOpen: string = ''

// Watch: Re-initialize map when modal is opened
watch(() => mapStore.mapModalOpen, (isOpen) => {
  if (!isOpen) {
    return
  }

  // Save limits before opening modal so we can detect changes made inside it
  limitsBeforeModalOpen = searchParamsStore.limits

  // Stamp the current history entry so popstate can identify this as a back-from-modal navigation
  history.replaceState({ ...history.state, backFromModal: true }, '')

  // Activate map search in modal (enables moveend events)
  mapStore.moveMapSearch = true

  // After opening modal and DOM update
  nextTick(() => {
    // Wait for modal animation (UiModal uses transitions)
    setTimeout(() => {
      // Recalculate map size
      if (modalMapRef.value?.invalidateMapSize) {
        modalMapRef.value.invalidateMapSize()
      }
    }, 350) // 350ms = modal animation duration + buffer
  })
})
/**
 * Handler for modal close events (X-button and browser back)
 * Includes debounce protection against multiple calls
 */
function handleModalClose (_reason?: string) {
  // Debounce: If a timer is already running, abort
  if (handleModalCloseTimer) {
    return
  }

  // Reset modal status
  mapStore.mapModalOpen = false

  // Deactivate moveMapSearch
  mapStore.moveMapSearch = false

  // Debounce timer: allow next call after 500ms
  handleModalCloseTimer = setTimeout(() => {
    handleModalCloseTimer = null
  }, 500)
}
onUnmounted(() => {
  if (handleModalCloseTimer) {
    clearTimeout(handleModalCloseTimer)
    handleModalCloseTimer = null
  }
})

const hitsText = data.value?.data?.hits !== 0 ? data.value?.data?.hits + ' ' : ''
if (props.showOnlyLastminuteObjects) {
  if (import.meta.client) {
    window.dataLayer.unshift({ content_group: 'lastminute' })
  }
  const location = data.value?.data?.location
  useHead({
    title: t.value('last_minute_title', { ort: location?.ort, preposition: location?.preposition }),
    meta: [
      { name: 'description', content: t.value('last_minute_description', { ort: location?.ort, preposition: location?.preposition }) }
    ]
  })
} else {
  if (import.meta.client) {
    window.dataLayer.unshift({ content_group: 'serp' })
  }
  useHead({
    title: props.pagedata?.urldata?.head?.meta[0].content ?? links.portal_domain_name + ': ' + hitsText + t.value('holiday_apartments_homes')
  })
}
if (import.meta.client) {
  watch(
    searchParamsStore.$state,
    (state) => {
      mapStore.lastOpenObject = null
      if (
        typeof data.value?.data?.params === 'object' &&
        SearchParamsHelper.areSearchParamsObjectsIdentical(data.value?.data?.params, state)
      ) {
        return
      }
      window.setTimeout(function () {
        const previousPage = urlHashSearchParams.getPageNumber()

        refresh()

        // When a filter is changed from a page other than the first
        // the other call to sendSearchRequestEvent() is triggered.
        if (previousPage > 1) {
          return
        }
        trackingSearchRequest()
      }, 300)
    },
    { deep: true }
  )
  trackingSearchRequest()
  watch(data, () => {
    if ((data.value?.data?.hits ?? 0) === 0) {
      const { $gtmModules } = useNuxtApp()
      $gtmModules.searchResults.sendNoSearchResultsDynamicEvent()
    }
  })
}

function trackingSearchRequest () {
  const { $gtmModules } = useNuxtApp()
  const { currentPortal } = usePortals()
  const config = currentPortal.value.search_form
  if (config && config.places) {
    const foundOrt = findOrtById(Object.values(config.places), searchParamsStore.ort)
    $gtmModules.searchRequests.sendSearchRequestEvent(searchParamsStore.ort, foundOrt?.name)
  } else {
    $gtmModules.searchRequests.sendSearchRequestEvent(searchParamsStore.ort, searchParamsStore.ort)
  }
}
function findOrtById (array, id) {
  return array.find(item => item.id === id)
}

const paginationCurrentPage: Ref<number> = ref(props.paginationCurrentPage)
if (import.meta.client) {
  searchParamsStore.$subscribe((mutation, state) => {
    paginationCurrentPage.value = state.page + 1
  })
}

function getUrlHashSearchParams (backFromModal: boolean = false) {
  if (urlHashSearchParams.getPathPrefix() === null || urlHashSearchParams.hasSsrParamsPrefix()) {
    return
  }
  const urlSsrParams = urlHashSearchParams.getSsr()
  if (urlSsrParams !== undefined) {
    searchParamsStore.replaceWithNewSearchParams(urlSsrParams, true, props.showOnlyLastminuteObjects)
    return
  }
  const urlHashParams = urlHashSearchParams.get()
  if (urlHashParams !== undefined) {
    searchParamsStore.replaceWithNewSearchParams(urlHashParams, true, props.showOnlyLastminuteObjects)
    return
  }
  // No hash params in URL — use appropriate reset depending on navigation context
  if (backFromModal) {
    // Coming back from map dialog via browser back button: preserve limits (map bounding box)
    searchParamsStore.resetToDefaultStateExceptOrtVonBisPersonenLimits(true, props.showOnlyLastminuteObjects)
  } else {
    searchParamsStore.resetToDefaultState(true, props.showOnlyLastminuteObjects)
  }
}
onMounted(() => {
  getUrlHashSearchParams()
  window.addEventListener('popstate', (event) => {
    if (urlHashSearchParams.hasSsrParamsPrefix()) {
      window.location.reload()
      return
    }
    if (!event.state?.backFromModal) {
      getUrlHashSearchParams()
      return
    }
    // Coming back from map modal via browser back button.
    // Capture the limits the user set inside the modal before getUrlHashSearchParams
    // overwrites the store from the (stale) URL hash.
    const limitsFromModal = searchParamsStore.limits

    // Restore pre-modal URL state into the store (preserves limits as fallback)
    getUrlHashSearchParams(true)

    // If the user panned the map inside the modal, the limits changed.
    // Restore those limits over the reset and update the URL to reflect them.
    if (limitsFromModal !== limitsBeforeModalOpen) {
      searchParamsStore.limits = limitsFromModal
      urlHashSearchParams.set(
        searchParamsStore.$state.page + 1,
        searchParamsStore.activeFilters
      )
    }

    if (window.innerWidth < BREAKPOINT_LG_PX) {
      if (mapStore.moveMapIteration === 0) {
        mapStore.fullSize = false
        mapStore.visible = false
      }
      mapStore.moveMapIteration--
    }
  }, false)
})

const highlightedObjectNr: Ref<number> = ref(-1)
const highlightedObjectIconUrl: Ref<string> = ref('')
function mapMarkerMouseOver (args: { data: MapMarkerEventData }) {
  highlightedObjectNr.value = args.data.objectNr
  highlightedObjectIconUrl.value = args.data.iconUrl
}
function mapMarkerMouseOut () {
  highlightedObjectNr.value = -1
  highlightedObjectIconUrl.value = ''
}
function mapMarkerClick (args: { data: MapMarkerEventData }) {
  const element = document.getElementById(`objekt-${args.data.objectNr}`)
  if (element) {
    element.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }
}
const { isMatching: isLgViewport } = useMediaQuery(BREAKPOINT_LG_MIN_QUERY)
const searchStickyResizeObserver = shallowRef<ResizeObserver | null>(null)

function showMap () {
  if (window.innerWidth >= BREAKPOINT_LG_PX) {
    mapStore.visible = true
  }
}

function updateStickyMapTop () {
  const mapSticky = document.querySelector('[data-behaviour="SearchResultMapSticky"]') as HTMLElement | null
  if (!mapSticky) {
    return
  }
  if (!isLgViewport.value) {
    mapSticky.style.top = '0px'
    return
  }
  const stickyStack = document.querySelector('[data-behaviour="SearchResultStickyStack"]') as HTMLElement | null
  const top = stickyStack?.offsetHeight ?? 0
  mapSticky.style.top = `${top}px`
  const mapHeightContainer = mapSticky.querySelector('.custom-map-height') as HTMLElement | null
  if (mapHeightContainer) {
    mapHeightContainer.style.height = `calc(100vh - ${top}px)`
  }
}

function observeSearchStickyElements () {
  searchStickyResizeObserver.value?.disconnect()
  searchStickyResizeObserver.value = new ResizeObserver(() => {
    updateStickyMapTop()
  })
  const stickyStack = document.querySelector('[data-behaviour="SearchResultStickyStack"]') as HTMLElement | null
  if (stickyStack) {
    searchStickyResizeObserver.value.observe(stickyStack)
  }
  updateStickyMapTop()
}

function onSearchPageWindowResize () {
  showMap()
  updateStickyMapTop()
}

if (import.meta.client) {
  showMap()
  if (window.innerWidth < BREAKPOINT_LG_PX) {
    mapStore.visible = false
  }
}
onMounted(() => {
  window.addEventListener('resize', onSearchPageWindowResize)
  nextTick(() => {
    observeSearchStickyElements()
  })
  watch(isLgViewport, updateStickyMapTop)
  watch(() => mapStore.visible, () => {
    nextTick(() => {
      observeSearchStickyElements()
    })
  })
})
onUnmounted(() => {
  window.removeEventListener('resize', onSearchPageWindowResize)
  searchStickyResizeObserver.value?.disconnect()
  searchStickyResizeObserver.value = null
})

if (import.meta.client) {
  watch(pending, async (isPending) => {
    const containerSearch = document.getElementById('container-search')
    if (isPending) {
      if (containerSearch) {
        containerSearch.style.minHeight = `${containerSearch.offsetHeight}px`
      }
      await nextTick()
      window.scrollTo(0, 0)
      return
    }
    if (containerSearch) {
      containerSearch.style.minHeight = ''
    }
  })
}
</script>

<style>
@reference "~/assets/css/main.css";
@media screen and (max-width: 976px) {
  #map-search {
    z-index: 1000;
  }
}
html, body {
  scroll-behavior: auto;
}

.custom-map-height{
  height: calc(100vh - 9.974rem);
}

/* Desktop: only left corners rounded (top-left + bottom-left); right edge square */
@media screen and (min-width: 976px) {
  .custom-region-map {
    overflow: hidden;
    border-top-left-radius: 0.75rem;
    border-bottom-left-radius: 0.75rem;
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
  }

  .custom-region-map .leaflet-container,
  .custom-region-map .custom-leaflet-container {
    border-top-left-radius: 0.75rem;
    border-bottom-left-radius: 0.75rem;
    border-top-right-radius: 0 !important;
    border-bottom-right-radius: 0 !important;
  }
}

.search-page-content {
  h1{
    @apply text-3xl md:text-5xl font-bold mt-12 mb-12;
  }
  h2{
    @apply text-2xl md:text-4xl font-semibold mt-12 mb-4;
  }
  h3{
    @apply text-xl md:text-2xl font-semibold mt-12 mb-4;
  }
  ul{
    @apply list-disc ml-5;
  }
  ol{
    @apply list-decimal ml-5;
  }
  a{
    @apply text-petrol-500 underline hover:text-petrol-800
  }
}
</style>
